"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import {
  Sparkles,
  Cpu,
  ChevronDown,
  FileText,
  Plus,
  BookOpen,
  StickyNote,
  Brain,
  Check,
  X,
  GraduationCap,
  Loader2,
  Paperclip,
  FolderTree,
  MessageSquare,
} from "lucide-react";
import {
  fetchAllProgress,
  fetchMasteryMap,
  type ProgressSummary,
  type MasteryMapResult,
  type MapKnowledgePoint,
} from "@/lib/learning-api";
import {
  listLLMOptions,
  llmSelectionKey,
  type LLMOption,
} from "@/lib/llm-options";
import type { LLMSelection } from "@/lib/unified-ws";
import {
  listTalkNoteDocuments,
  type TalkNoteDocumentSummary,
} from "@/lib/talk-note-api";
import { apiFetch, apiUrl } from "@/lib/api";
import PersonaPicker from "@/components/chat/PersonaPicker";
import { bookApi } from "@/lib/book-api";
import { listKnowledgeBases } from "@/lib/knowledge-api";
import {
  UNCATEGORIZED_KEY,
  groupByKnowledge,
} from "@/lib/knowledge-grouping";
import { KnowledgeScopeSelect } from "@/components/knowledge/KnowledgeScopeSelect";
import {
  buildHomeKnowledgeScopes,
  toActiveKnowledge,
  toHomeKnowledgeScopeOptions,
} from "@/lib/home-knowledge-scope";
import { listSessions, type SessionSummary } from "@/lib/session-api";
import { useAppShell } from "@/context/AppShellContext";
import {
  buildBookKnowledgePointHref,
  buildNextStepHref,
  buildStudyHref as buildStudyLaunchHref,
  nextStepCtaKey,
} from "@/lib/next-step-routing";

// Shape of GET /api/v1/memory/overview — only the fields home reads.
type MemoryOverview = { docs: { backlog: number }[] };


// Serpentine layout constants for the knowledge-cell map (deterministic — no random).
const VB_W = 620;
const VB_H = 430;
const COLS = 4;

type CellNode = {
  kp: MapKnowledgePoint;
  moduleName: string;
  x: number;
  y: number;
};

function layoutCells(map: MasteryMapResult | null): {
  nodes: CellNode[];
  edges: { x1: number; y1: number; x2: number; y2: number }[];
} {
  if (!map) return { nodes: [], edges: [] };
  const flat: { kp: MapKnowledgePoint; moduleName: string }[] = [];
  for (const mod of [...map.map.modules].sort((a, b) => a.order - b.order)) {
    for (const kp of mod.knowledge_points) flat.push({ kp, moduleName: mod.name });
  }
  const capped = flat.slice(0, 12); // keep the map readable; count pills show the true totals
  const marginX = 88;
  const marginY = 66;
  const stepX = (VB_W - marginX * 2) / (COLS - 1);
  const stepY = 112;
  const nodes: CellNode[] = capped.map(({ kp, moduleName }, i) => {
    const row = Math.floor(i / COLS);
    const rawCol = i % COLS;
    const col = row % 2 === 1 ? COLS - 1 - rawCol : rawCol; // boustrophedon snake
    const x = marginX + col * stepX + Math.sin(i * 1.7) * 14;
    const y = marginY + row * stepY + Math.cos(i * 2.3) * 12;
    return { kp, moduleName, x, y };
  });
  const edges = [];
  for (let i = 0; i < nodes.length - 1; i++) {
    edges.push({
      x1: nodes[i].x,
      y1: nodes[i].y,
      x2: nodes[i + 1].x,
      y2: nodes[i + 1].y,
    });
  }
  return { nodes, edges };
}

// English source strings; rendered through i18next `t()` at the call site.
const STATUS_LABEL: Record<string, string> = {
  mastered: "Complete",
  learning: "In progress",
  new: "Locked",
};

function fmtDate(
  epoch: number,
  t: (key: string, opts?: Record<string, unknown>) => string,
  locale: string,
): string {
  if (!epoch) return "";
  const d = new Date(epoch * 1000);
  const now = Date.now();
  const days = Math.floor((now - d.getTime()) / 86400000);
  if (days <= 0) return t("Today");
  if (days === 1) return t("Yesterday");
  if (days < 7) return t("{{days}}d ago", { days });
  return d.toLocaleDateString(locale, { month: "long", day: "numeric" });
}

export default function HomeDashboard() {
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith("ko") ? "ko-KR" : "en-US";
  const { activeKnowledge, setActiveKnowledge } = useAppShell();

  const [summaries, setSummaries] = useState<ProgressSummary[]>([]);
  const [progressErrors, setProgressErrors] = useState<Set<string>>(new Set());
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [selectedBookId, setSelectedBookId] = useState<string | null>(null);

  const [map, setMap] = useState<MasteryMapResult | null>(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);

  const [notes, setNotes] = useState<TalkNoteDocumentSummary[]>([]);
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [bookKnowledgeBases, setBookKnowledgeBases] = useState<Map<
    string,
    string[]
  > | null>(null);
  const [knowledgeNames, setKnowledgeNames] = useState<string[]>([]);
  const [knowledgeAssetsLoading, setKnowledgeAssetsLoading] = useState(true);
  const [selectedKnowledge, setSelectedKnowledge] = useState<string | null>(null);
  const scopeDefaultApplied = useRef(false);

  const [selectedKp, setSelectedKp] = useState<CellNode | null>(null);

  const [pendingPersona, setPendingPersona] = useState<string | null>(null); // B3
  const [pendingEngine, setPendingEngine] = useState<LLMSelection | null>(null); // B2

  const [llmOptions, setLlmOptions] = useState<LLMOption[]>([]);
  const [personaOpen, setPersonaOpen] = useState(false);
  const [engineOpen, setEngineOpen] = useState(false);

  const [dismissedTidy, setDismissedTidy] = useState<Set<string>>(new Set());
  const [backlogTotal, setBacklogTotal] = useState(0);

  // Load courses, knowledge-scoped assets, memory, and engine options once.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetchAllProgress();
        if (!alive) return;
        setSummaries(res.summaries);
        setProgressErrors(new Set(res.errors.map((e) => e.book_id)));
      } catch {
        if (alive) setSummaries([]);
      } finally {
        if (alive) setCoursesLoading(false);
      }
    })();
    (async () => {
      const [booksResult, notesResult, sessionsResult, knowledgeResult] =
        await Promise.allSettled([
          bookApi.list(),
          listTalkNoteDocuments(),
          listSessions(200, 0, { force: true }),
          listKnowledgeBases({ force: true }),
        ]);
      if (!alive) return;

      if (booksResult.status === "fulfilled") {
        setBookKnowledgeBases(
          new Map(
            booksResult.value.books.map((book) => [
              book.id,
              book.knowledge_bases,
            ]),
          ),
        );
      }
      if (notesResult.status === "fulfilled") setNotes(notesResult.value);
      if (sessionsResult.status === "fulfilled") setSessions(sessionsResult.value);
      if (knowledgeResult.status === "fulfilled") {
        setKnowledgeNames(
          knowledgeResult.value
            .map((knowledge) => knowledge.name.trim())
            .filter(Boolean),
        );
      }
      setKnowledgeAssetsLoading(false);
    })();
    (async () => {
      // Metis backlog — T3 events not yet folded into memory. Real signal
      // (same source MemoryHub reads), so the home nudge stays honest.
      try {
        const r = await apiFetch(apiUrl("/api/v1/memory/overview"));
        const ov = (await r.json()) as MemoryOverview;
        if (alive)
          setBacklogTotal(
            (ov.docs || []).reduce((s, d) => s + (d.backlog || 0), 0),
          );
      } catch {
        /* memory summary is non-critical */
      }
    })();
    (async () => {
      try {
        const opts = await listLLMOptions();
        if (alive) setLlmOptions(opts.options);
      } catch {
        /* engine picker degrades to 기본 */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const courseGroups = useMemo(
    () =>
      bookKnowledgeBases
        ? groupByKnowledge(summaries, (summary) =>
            bookKnowledgeBases.get(summary.book_id),
          )
        : [],
    [bookKnowledgeBases, summaries],
  );
  const noteGroups = useMemo(
    () => groupByKnowledge(notes, (note) => note.knowledge_bases),
    [notes],
  );
  const sessionGroups = useMemo(
    () =>
      groupByKnowledge(
        sessions,
        (session) => session.preferences?.knowledge_bases,
      ),
    [sessions],
  );

  const knowledgeScopes = useMemo(
    () =>
      buildHomeKnowledgeScopes(
        knowledgeNames,
        courseGroups,
        noteGroups,
        sessionGroups,
      ),
    [courseGroups, knowledgeNames, noteGroups, sessionGroups],
  );
  const knowledgeScopeOptions = useMemo(
    () => toHomeKnowledgeScopeOptions(knowledgeScopes, t),
    [knowledgeScopes, t],
  );
  useEffect(() => {
    if (scopeDefaultApplied.current || knowledgeAssetsLoading) return;
    if (
      activeKnowledge &&
      knowledgeScopes.some(
        (scope) =>
          scope.key !== UNCATEGORIZED_KEY && scope.key === activeKnowledge,
      )
    ) {
      setSelectedKnowledge(activeKnowledge);
    }
    scopeDefaultApplied.current = true;
  }, [activeKnowledge, knowledgeAssetsLoading, knowledgeScopes]);

  const selectKnowledgeScope = useCallback(
    (scope: string | null) => {
      setSelectedKnowledge(scope);
      setActiveKnowledge(toActiveKnowledge(scope));
    },
    [setActiveKnowledge],
  );


  const visibleSummaries = useMemo(
    () =>
      selectedKnowledge === null
        ? summaries
        : courseGroups.find((group) => group.key === selectedKnowledge)?.items ??
          [],
    [courseGroups, selectedKnowledge, summaries],
  );
  const visibleNotes = useMemo(
    () =>
      selectedKnowledge === null
        ? notes
        : noteGroups.find((group) => group.key === selectedKnowledge)?.items ??
          [],
    [noteGroups, notes, selectedKnowledge],
  );
  const visibleSessions = useMemo(
    () =>
      selectedKnowledge === null
        ? sessions
        : sessionGroups.find((group) => group.key === selectedKnowledge)
            ?.items ?? [],
    [selectedKnowledge, sessionGroups, sessions],
  );

  useEffect(() => {
    if (
      selectedKnowledge !== null &&
      !knowledgeScopes.some((scope) => scope.key === selectedKnowledge)
    ) {
      setSelectedKnowledge(null);
    }
  }, [knowledgeScopes, selectedKnowledge]);

  useEffect(() => {
    if (coursesLoading) return;
    if (
      selectedBookId &&
      visibleSummaries.some((summary) => summary.book_id === selectedBookId)
    ) {
      return;
    }
    setSelectedBookId(visibleSummaries[0]?.book_id ?? null);
  }, [coursesLoading, selectedBookId, visibleSummaries]);

  // Load the mastery map whenever the selected course changes.
  useEffect(() => {
    if (
      !selectedBookId ||
      !visibleSummaries.some((summary) => summary.book_id === selectedBookId)
    ) {
      setMap(null);
      setMapLoading(false);
      setMapError(null);
      setSelectedKp(null);
      return;
    }
    let alive = true;
    setMapLoading(true);
    setMapError(null);
    setSelectedKp(null);
    (async () => {
      try {
        const res = await fetchMasteryMap(selectedBookId);
        if (alive) setMap(res);
      } catch {
        if (alive) setMapError(t("Couldn't load the knowledge map"));
      } finally {
        if (alive) setMapLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [selectedBookId, visibleSummaries]);

  const selectedCourse = useMemo(
    () =>
      visibleSummaries.find((summary) => summary.book_id === selectedBookId) ??
      null,
    [selectedBookId, visibleSummaries],
  );

  const { nodes, edges } = useMemo(() => layoutCells(map), [map]);
  const nextStep = useMemo(() => {
    if (!map?.next) return null;

    let knowledgePointId: string | undefined;
    for (const courseModule of map.map.modules) {
      const knowledgePoint = courseModule.knowledge_points.find(
        (point) => point.name === map.next.knowledge_point_name,
      );
      if (knowledgePoint) {
        knowledgePointId = knowledgePoint.id;
        break;
      }
    }

    return {
      href: buildNextStepHref({
        action: map.next.action,
        bookId: selectedBookId,
        knowledgePointId,
      }),
      labelKey: nextStepCtaKey(map.next.action),
      complete: map.next.action === "complete",
    };
  }, [map, selectedBookId]);

  const engineLabel = useMemo(() => {
    if (!pendingEngine) return t("Default engine");
    const opt = llmOptions.find(
      (o) => llmSelectionKey(o) === llmSelectionKey(pendingEngine),
    );
    return opt ? opt.model_name || opt.model : t("Selected engine");
  }, [pendingEngine, llmOptions]);

  // 학습 중인 개념 — backend exposes only a due-count (map.due_reviews), not a
  // due list, and the demo review queue is empty. So surface the in-progress
  // (learning) KPs rather than mislabeling them as t("Due for review").
  const reviewItems = useMemo(() => {
    if (!map) return [];
    const out: { kp: MapKnowledgePoint; moduleName: string }[] = [];
    for (const mod of map.map.modules) {
      for (const kp of mod.knowledge_points) {
        if (kp.status === "learning") out.push({ kp, moduleName: mod.name });
      }
    }
    return out.slice(0, 5);
  }, [map]);

  // Metis 정리 제안 — memory backlog(진짜 T3 신호)를 최상단에, 그 아래로
  // 규칙기반 orphan 노트 + idle 코스. 본체 정리는 memory 화면에서.
  const tidyItems = useMemo(() => {
    const items: {
      id: string;
      kind: "orphan" | "idle" | "backlog";
      title: string;
      desc: string;
    }[] = [];
    if (backlogTotal > 0) {
      items.push({
        id: "backlog",
        kind: "backlog",
        title: t("{{count}} records not folded into memory yet", {
          count: backlogTotal,
        }),
        desc: t("Not yet distilled into memory from chats and notes"),
      });
    }
    for (const n of visibleNotes) {
      if (!n.knowledge_bases || n.knowledge_bases.length === 0) {
        items.push({
          id: `orphan:${n.id}`,
          kind: "orphan",
          title: n.title || t("Untitled note"),
          desc: t("Note not linked to a course"),
        });
      }
    }
    for (const s of visibleSummaries) {
      if (s.kp_count === 0) {
        items.push({
          id: `idle:${s.book_id}`,
          kind: "idle",
          title: s.name,
          desc: t("Course with no concepts yet"),
        });
      }
    }
    return items.filter((i) => !dismissedTidy.has(i.id));
  }, [visibleNotes, visibleSummaries, dismissedTidy, backlogTotal]);

  const buildStudyHref = useCallback(
    (bookId?: string) =>
      buildStudyLaunchHref({
        bookId: bookId ?? selectedBookId,
        bookKnowledgeBases,
        activeKnowledge,
        persona: pendingPersona,
        engine: pendingEngine,
      }),
    [
      activeKnowledge,
      bookKnowledgeBases,
      pendingEngine,
      pendingPersona,
      selectedBookId,
    ],
  );

  const dismissTidy = (id: string) =>
    setDismissedTidy((prev) => new Set(prev).add(id));

  const masteredCount = map?.map.counts.mastered ?? 0;
  const learningCount = map?.map.counts.learning ?? 0;
  const selectedScope =
    selectedKnowledge === null
      ? null
      : knowledgeScopes.find((scope) => scope.key === selectedKnowledge) ?? null;
  const selectedKnowledgeLabel = selectedScope?.label ?? t("All");

  return (
    <div className="dash">
      <div className="wrap">
        {/* ── Topbar ── */}
        <div className="topbar">
          <div className="greet">
            <h1>{t("Learning dashboard")}</h1>
            <p>{t("Keep studying your own course, built from a knowledge map made out of your documents")}</p>
          </div>
          <div className="grow" />
          <div className="setchips">
            <button
              className="setchip persona"
              onClick={() => setPersonaOpen(true)}
            >
              <span className="ic persona-ic">
                <Sparkles size={16} strokeWidth={1.75} />
              </span>
              <span className="tx">
                <span className="k">{t("Responder · Persona")}</span>
                <span className="v">{pendingPersona || t("Default TokTok tutor")}</span>
              </span>
              <ChevronDown size={14} strokeWidth={1.75} className="caret" />
            </button>

            <div className="engine-wrap">
              <button
                className="setchip engine"
                onClick={() => setEngineOpen((v) => !v)}
              >
                <span className="ic engine-ic">
                  <Cpu size={16} strokeWidth={1.75} />
                </span>
                <span className="tx">
                  <span className="k">{t("Engine")}</span>
                  <span className="v">{engineLabel}</span>
                </span>
                <ChevronDown size={14} strokeWidth={1.75} className="caret" />
              </button>
              {engineOpen && (
                <div className="popover" role="listbox">
                  <button
                    className={`pop-item ${!pendingEngine ? "on" : ""}`}
                    onClick={() => {
                      setPendingEngine(null);
                      setEngineOpen(false);
                    }}
                  >
                    <span>{t("Default engine")}</span>
                    {!pendingEngine && <Check size={14} strokeWidth={2} />}
                  </button>
                  {llmOptions.map((o) => {
                    const sel = llmSelectionKey(o) === llmSelectionKey(pendingEngine);
                    return (
                      <button
                        key={llmSelectionKey(o)}
                        className={`pop-item ${sel ? "on" : ""}`}
                        onClick={() => {
                          setPendingEngine({
                            profile_id: o.profile_id,
                            model_id: o.model_id,
                          });
                          setEngineOpen(false);
                        }}
                      >
                        <span className="pop-tx">
                          <b>{o.model_name || o.model}</b>
                          <em>{o.provider_label || o.provider}</em>
                        </span>
                        {sel && <Check size={14} strokeWidth={2} />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── 지식별 페이지 그룹 ── */}
        <section className="knowledge-scope" aria-labelledby="knowledge-scope-title">
          <div className="scope-head">
            <span className="scope-icon">
              <FolderTree size={17} strokeWidth={1.75} />
            </span>
            <div className="scope-copy">
              <h2 id="knowledge-scope-title">{t("Browse materials by knowledge")}</h2>
              <p>{t("Pick a knowledge base and its books, notes, chats and learning map switch with it")}</p>
            </div>
          </div>
          <div className="scope-select">
            <KnowledgeScopeSelect
              options={knowledgeScopeOptions}
              value={selectedKnowledge}
              onChange={selectKnowledgeScope}
              allLabel={t("All ({{count}} courses)", { count: summaries.length })}
            />
            {knowledgeAssetsLoading && knowledgeScopes.length === 0 ? (
              <span className="scope-loading">
                <Loader2 className="spin" size={14} /> {t("Loading knowledge groups…")}
              </span>
            ) : null}
          </div>
        </section>

        {/* ── 세포맵 hero ── */}
        <div className="hero">
          <div className="village">
            <div className="village-head">
              <div>
                <div className="t">{t("Knowledge cell map")}</div>
                <div className="sub">
                  {selectedCourse
                    ? t("{{course}} — a learning map where concepts grow like cells", {
                        course: selectedCourse.name,
                      })
                    : t("Select a course to draw its concept map")}
                </div>
              </div>
              {map && (
                <div className="counts">
                  <span className="cpill">
                    <span className="sw mastered" />
                    {t("Complete")} {map.map.counts.mastered}
                  </span>
                  <span className="cpill">
                    <span className="sw learning" />
                    {t("In progress")} {map.map.counts.learning}
                  </span>
                  <span className="cpill">
                    <span className="sw new" />
                    {t("Locked")} {map.map.counts.new}
                  </span>
                  <span className="cpill due">
                    {t("Review")} {map.map.due_reviews}
                  </span>
                </div>
              )}
            </div>

            {mapLoading ? (
              <div className="village-state">
                <Loader2 className="spin" size={22} strokeWidth={1.75} /> {t("Drawing the knowledge map…")}
              </div>
            ) : mapError ? (
              <div className="village-state err">{mapError}</div>
            ) : !selectedCourse ? (
              <div className="village-state">
                {selectedKnowledge === null
                  ? t("Pick a course below, or create a new one to start")
                  : t("No courses linked to {{knowledge}}", { knowledge: selectedKnowledgeLabel })}
              </div>
            ) : nodes.length === 0 ? (
              <div className="village-state">{t("This course has no concepts yet")}</div>
            ) : (
              <svg className="graph" viewBox={`0 0 ${VB_W} ${VB_H}`}>
                {edges.map((e, i) => (
                  <line
                    key={i}
                    className="edge"
                    x1={e.x1}
                    y1={e.y1}
                    x2={e.x2}
                    y2={e.y2}
                  />
                ))}
                {nodes.map((n, i) => {
                  const s = n.kp.status;
                  const sel = selectedKp?.kp.id === n.kp.id;
                  return (
                    <g
                      key={n.kp.id}
                      className={`node ${s} ${sel ? "sel" : ""}`}
                      transform={`translate(${n.x},${n.y})`}
                      onClick={() => setSelectedKp(n)}
                    >
                      {s !== "new" && <circle className="membrane" r={26} />}
                      <circle className="cell" r={21} />
                      <circle className="core" r={4} />
                      <text className="lbl" y={40}>
                        {n.kp.name.length > 10
                          ? n.kp.name.slice(0, 9) + "…"
                          : n.kp.name}
                      </text>
                    </g>
                  );
                })}
              </svg>
            )}
            <div className="legend">
              <span>
                <span className="ln" />{t("Prerequisite (learning order)")}
              </span>
            </div>
          </div>

          {/* right rail */}
          <div className="hrail">
            <div className="panel kp">
              {selectedKp ? (
                <>
                  <div className="kp-name">{selectedKp.kp.name}</div>
                  <div className="kp-meta">
                    <span className={`tag ${selectedKp.kp.status}`}>
                      {STATUS_LABEL[selectedKp.kp.status]
                        ? t(STATUS_LABEL[selectedKp.kp.status])
                        : selectedKp.kp.status}
                    </span>
                    <span className="kp-type">{selectedKp.kp.type}</span>
                  </div>
                  <div className="mrow">
                    <span>{t("Understanding")}</span>
                    <b>{Math.round(selectedKp.kp.mastery * 100)}%</b>
                  </div>
                  <div className="mbar">
                    <i
                      className={selectedKp.kp.status}
                      style={{
                        width: `${Math.round(selectedKp.kp.mastery * 100)}%`,
                      }}
                    />
                  </div>
                  <div className="kp-mod">{selectedKp.moduleName}</div>
                  <button
                    type="button"
                    className="kp-book-cta"
                    onClick={() =>
                      router.push(
                        buildBookKnowledgePointHref(
                          selectedBookId,
                          selectedKp.kp.id,
                        ),
                      )
                    }
                  >
                    <BookOpen size={14} strokeWidth={1.75} />
                    {t(t("Open in the book"))}
                  </button>
                </>
              ) : (
                <div className="kp-empty">
                  {t("Click a cell to see its concept details here")}
                </div>
              )}
            </div>

            {map?.next && (
              <div className="panel next">
                <div className="action">{t("Today's picks")}</div>
                <div className="kpname">{map.next.knowledge_point_name}</div>
                <div className="reason">{map.next.reason}</div>
                {nextStep?.complete && (
                  <div className="next-complete">
                    <Check size={14} strokeWidth={2} />
                    {t(t("You've finished all of today's picks"))}
                  </div>
                )}
                <div className="thr">
                  <i
                    style={{
                      width: `${Math.round((map.next.mastery ?? 0) * 100)}%`,
                    }}
                  />
                </div>
              </div>
            )}

            <div className="launch">
              <div className="attachrow">
                <button className="attach" disabled title={t("Attach material (coming soon)")}>
                  <Paperclip size={14} strokeWidth={1.75} />
                  {t("Attach material")}
                </button>
              </div>
              <button
                className="start"
                disabled={!nextStep && !selectedCourse}
                onClick={() =>
                  router.push(nextStep?.href ?? buildStudyHref())
                }
              >
                <GraduationCap size={17} strokeWidth={1.75} />
                {t(nextStep?.labelKey ?? t("Open a self-study chat"))}
              </button>
              {nextStep && (
                <button
                  className="study-link"
                  disabled={!selectedCourse}
                  onClick={() => router.push(buildStudyHref())}
                >
                  {t(t("Open a self-study chat"))}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── 내 코스 ── */}
        <div className="sec">
          <h2>{t("My courses")}</h2>
          <span className="hint">
            {selectedKnowledge === null
              ? t("Pick a knowledge map to draw the cell map above")
              : t("Courses linked to {{knowledge}}", {
                  knowledge: selectedKnowledgeLabel,
                })}
          </span>
          <span className="grow" />
        </div>
        <div className="courses">
          {coursesLoading ? (
            <div className="course-skel">
              <Loader2 className="spin" size={20} strokeWidth={1.75} /> {t("Loading courses…")}
            </div>
          ) : visibleSummaries.length === 0 ? (
            <div className="course-empty">
              <FolderTree size={18} strokeWidth={1.6} />
              <span>
                {t("No courses linked to {{knowledge}} yet", {
                  knowledge: selectedKnowledgeLabel,
                })}
              </span>
            </div>
          ) : (
            visibleSummaries.map((s) => {
              const on = s.book_id === selectedBookId;
              const failed = progressErrors.has(s.book_id);
              return (
                <button
                  key={s.book_id}
                  className={`course ${on ? "on" : ""}`}
                  onClick={() => setSelectedBookId(s.book_id)}
                >
                  {on && <span className="badge-on">{t("Selected")}</span>}
                  <span className="glyph">
                    <BookOpen size={18} strokeWidth={1.75} />
                  </span>
                  <h3>{s.name}</h3>
                  <div className="src">
                    <FileText size={13} strokeWidth={1.75} />
                    {t("{{modules}} modules · {{concepts}} concepts", {
                      modules: s.modules_count,
                      concepts: s.kp_count,
                    })}
                  </div>
                  <div className="stat">
                    {t("Understanding")} <b>{Math.round(s.avg_mastery_pct)}%</b>
                  </div>
                  <div className="mbar">
                    <i style={{ width: `${Math.min(100, s.avg_mastery_pct)}%` }} />
                  </div>
                  <div className="stage">
                    <span className="dot" />
                    {failed ? t("Load error") : s.current_stage} ·{" "}
                    {fmtDate(s.updated_at, t, dateLocale)}
                  </div>
                </button>
              );
            })
          )}
          <button className="course new" onClick={() => router.push("/book")}>
            <span className="ring">
              <Plus size={20} strokeWidth={2} />
            </span>
            <h3>{t("Create a course")}</h3>
            <p>{t("Drop in PDFs, handwriting or lecture material and it builds a knowledge map and lays out a course")}</p>
          </button>
        </div>

        {/* ── 지식에 연결된 자료와 학습 현황 ── */}
        <div className="grid4">
          {/* 내 노트 */}
          <div className="col-card">
            <div className="ch">
              <StickyNote size={16} strokeWidth={1.75} /> {t("My notes")}
            </div>
            {visibleNotes.length === 0 ? (
              <div className="col-empty">
                {selectedKnowledge === null
                  ? t("No notes yet")
                  : t("No notes linked to {{knowledge}}", { knowledge: selectedKnowledgeLabel })}
              </div>
            ) : (
              visibleNotes.slice(0, 5).map((n) => (
                <button
                  key={n.id}
                  className="note-item"
                  onClick={() => router.push(`/talk-note`)}
                >
                  <span className="ni">
                    <FileText size={15} strokeWidth={1.75} />
                  </span>
                  <span className="nb">
                    <span className="nt">{n.title || t("Untitled note")}</span>
                    <span className="nk">
                      {n.knowledge_bases.length > 0 ? (
                        n.knowledge_bases.slice(0, 2).map((kb) => (
                          <span key={kb} className="kbchip">
                            {kb}
                          </span>
                        ))
                      ) : (
                        <span className="kbchip muted">{t("Not linked")}</span>
                      )}
                      <span className="np">{fmtDate(n.updated_at, t, dateLocale)}</span>
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>

          {/* 최근 대화 */}
          <div className="col-card">
            <div className="ch">
              <MessageSquare size={16} strokeWidth={1.75} /> {t("Recent chats")}
            </div>
            {visibleSessions.length === 0 ? (
              <div className="col-empty">
                {selectedKnowledge === null
                  ? t("No saved chats yet")
                  : t("No chats linked to {{knowledge}}", { knowledge: selectedKnowledgeLabel })}
              </div>
            ) : (
              visibleSessions.slice(0, 5).map((session) => {
                const knowledgeBases = session.preferences?.knowledge_bases ?? [];
                return (
                  <button
                    key={session.session_id}
                    className="note-item"
                    onClick={() =>
                      router.push(
                        `/study/${encodeURIComponent(session.session_id)}`,
                      )
                    }
                  >
                    <span className="ni chat">
                      <MessageSquare size={15} strokeWidth={1.75} />
                    </span>
                    <span className="nb">
                      <span className="nt">{session.title || t("Untitled chat")}</span>
                      <span className="chat-preview">
                        {session.last_message || t("No messages")}
                      </span>
                      <span className="nk">
                        {knowledgeBases.length > 0 ? (
                          knowledgeBases.slice(0, 2).map((name) => (
                            <span key={name} className="kbchip">
                              {name}
                            </span>
                          ))
                        ) : (
                          <span className="kbchip muted">{t("Not linked")}</span>
                        )}
                        <span className="np">{fmtDate(session.updated_at, t, dateLocale)}</span>
                      </span>
                    </span>
                  </button>
                );
              })
            )}
          </div>

          {/* 학습 중인 개념 */}
          <div className="col-card">
            <div className="ch">
              <BookOpen size={16} strokeWidth={1.75} /> {t("Concepts in progress")}
            </div>
            {reviewItems.length === 0 ? (
              <div className="col-empty">{t("No concepts in progress")}</div>
            ) : (
              reviewItems.map(({ kp, moduleName }) => (
                <div key={kp.id} className="due-item">
                  <span className="sw learning" />
                  <span className="dn">
                    <b>{kp.name}</b>
                    <em>{moduleName}</em>
                  </span>
                  <button
                    className="due-cta"
                    onClick={() => router.push(buildStudyHref())}
                  >
                    {t("Continue")}
                  </button>
                </div>
              ))
            )}
          </div>

          {/* 기억·정리 제안 */}
          <div className="col-card memory-card">
            <div className="ch">
              <Brain size={16} strokeWidth={1.75} /> {t("Memory & tidy-up suggestions")}
            </div>
            <div className="mem-summary">
              {selectedCourse ? (
                <>
                  <div className="ms-line">
                    {t("In this course")} <b>{masteredCount}</b>{" "}
                    {t("Concepts mastered")}
                  </div>
                  <div className="ms-sub">
                    {t("{{count}} in progress · keep going", { count: learningCount })}
                  </div>
                </>
              ) : (
                <>
                  <div className="ms-line">{t("No linked courses")}</div>
                  <div className="ms-sub">{t("Create a course from a knowledge group to see its status here")}</div>
                </>
              )}
            </div>
            <div className="tidy-head">
              {t("Tidy-up suggestion")} <span className="cnt">{tidyItems.length}</span>
            </div>
            {tidyItems.length === 0 ? (
              <div className="col-empty sm">{t("Nothing to tidy up")}</div>
            ) : (
              tidyItems.slice(0, 4).map((item) => (
                <div key={item.id} className="tidy">
                  <span className={`ty-ic ${item.kind}`}>
                    {item.kind === "backlog" ? (
                      <Brain size={13} strokeWidth={1.75} />
                    ) : item.kind === "orphan" ? (
                      <StickyNote size={13} strokeWidth={1.75} />
                    ) : (
                      <BookOpen size={13} strokeWidth={1.75} />
                    )}
                  </span>
                  <span className="ty-b">
                    <span className="ty-t">{item.title}</span>
                    <span className="ty-d">{item.desc}</span>
                  </span>
                  <span className="ty-act">
                    <button
                      className="ty-y"
                      title={item.kind === "backlog" ? t("Go tidy up") : t("Accept")}
                      onClick={() =>
                        item.kind === "backlog"
                          ? router.push("/memory")
                          : dismissTidy(item.id)
                      }
                    >
                      <Check size={13} strokeWidth={2} />
                    </button>
                    <button
                      className="ty-n"
                      title={t("Dismiss")}
                      onClick={() => dismissTidy(item.id)}
                    >
                      <X size={13} strokeWidth={2} />
                    </button>
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <PersonaPicker
        open={personaOpen}
        initialPersona={pendingPersona}
        onClose={() => setPersonaOpen(false)}
        onApply={(sel) => {
          setPendingPersona(sel);
          setPersonaOpen(false);
        }}
      />

      <style jsx>{`
        .dash {
          height: 100%;
          overflow: auto;
          background: var(--background);
          color: var(--foreground);
        }
        .wrap {
          max-width: 1080px;
          margin: 0 auto;
          padding: 22px 28px 60px;
        }
        .grow {
          flex: 1;
        }
        .spin {
          animation: sp 0.9s linear infinite;
        }
        @keyframes sp {
          to {
            transform: rotate(360deg);
          }
        }

        /* topbar */
        .topbar {
          display: flex;
          align-items: flex-end;
          gap: 14px;
          margin-bottom: 26px;
        }
        .greet h1 {
          font-size: 26px;
          font-weight: 800;
          letter-spacing: -0.02em;
        }
        .greet p {
          color: var(--muted-foreground);
          font-size: 13.5px;
          margin-top: 4px;
        }
        .setchips {
          display: flex;
          gap: 10px;
        }
        .engine-wrap {
          position: relative;
        }
        .setchip {
          display: flex;
          align-items: center;
          gap: 9px;
          height: 46px;
          padding: 0 12px;
          border: 1px solid var(--border);
          border-radius: 12px;
          background: var(--card);
          cursor: pointer;
          transition: border-color 0.15s var(--ease), box-shadow 0.15s var(--ease);
        }
        .setchip:hover {
          border-color: var(--ring);
        }
        .setchip .ic {
          width: 28px;
          height: 28px;
          display: grid;
          place-items: center;
          border-radius: 8px;
          color: var(--primary);
        }
        .persona-ic {
          background: color-mix(in srgb, var(--spark) 18%, var(--card));
          color: var(--spark);
        }
        .engine-ic {
          background: color-mix(in srgb, var(--primary) 15%, var(--card));
        }
        .setchip .tx {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          line-height: 1.2;
        }
        .setchip .k {
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: var(--muted-foreground);
        }
        .setchip .v {
          font-size: 13.5px;
          font-weight: 600;
          max-width: 150px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .setchip .caret {
          color: var(--muted-foreground);
        }
        .popover {
          position: absolute;
          top: 52px;
          right: 0;
          z-index: 30;
          min-width: 240px;
          max-height: 320px;
          overflow: auto;
          background: var(--card);
          border: 1px solid var(--border);
          border-radius: 12px;
          box-shadow: var(--shadow-soft);
          padding: 6px;
        }
        .pop-item {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          padding: 9px 10px;
          border-radius: 8px;
          background: transparent;
          cursor: pointer;
          color: var(--foreground);
          text-align: left;
        }
        .pop-item:hover {
          background: var(--muted);
        }
        .pop-item.on {
          background: color-mix(in srgb, var(--primary) 12%, var(--card));
          color: var(--primary);
        }
        .pop-tx {
          display: flex;
          flex-direction: column;
        }
        .pop-tx b {
          font-size: 13.5px;
          font-weight: 600;
        }
        .pop-tx em {
          font-size: 13px;
          font-style: normal;
          color: var(--muted-foreground);
        }

        /* knowledge scope */
        .knowledge-scope {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 18px;
          padding: 14px;
          border: 1px solid var(--border);
          border-radius: 16px;
          background: color-mix(in srgb, var(--card) 88%, var(--secondary));
        }
        .scope-head {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .scope-icon {
          width: 32px;
          height: 32px;
          flex: none;
          display: grid;
          place-items: center;
          border-radius: 9px;
          color: var(--primary);
          background: color-mix(in srgb, var(--primary) 12%, var(--card));
        }
        .scope-copy {
          min-width: 0;
        }
        .scope-copy h2 {
          font-size: 14px;
          font-weight: 700;
        }
        .scope-copy p {
          margin-top: 2px;
          color: var(--muted-foreground);
          font-size: 13.5px;
        }
        .scope-select {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
        }
        .scope-loading {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 8px 6px;
          color: var(--muted-foreground);
          font-size: 13.5px;
          white-space: nowrap;
        }

        /* section header */
        .sec {
          display: flex;
          align-items: baseline;
          gap: 10px;
          margin: 8px 0 12px;
        }
        .sec h2 {
          font-size: 18px;
          font-weight: 700;
        }
        .sec .hint {
          font-size: 13.5px;
          color: var(--muted-foreground);
        }

        /* courses */
        .courses {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(232px, 1fr));
          gap: 14px;
          margin-bottom: 28px;
        }
        .course-skel {
          grid-column: 1 / -1;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 28px;
          color: var(--muted-foreground);
          font-size: 14px;
        }
        .course-empty {
          grid-column: 1 / -1;
          min-height: 112px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 9px;
          padding: 24px;
          border: 1px dashed var(--border);
          border-radius: 16px;
          color: var(--muted-foreground);
          font-size: 13.5px;
        }
        .course {
          position: relative;
          text-align: left;
          border: 1px solid var(--border);
          border-radius: 16px;
          background: var(--card);
          padding: 16px;
          cursor: pointer;
          transition: box-shadow 0.18s var(--ease), transform 0.18s var(--ease);
        }
        .course:hover {
          transform: translateY(-2px);
        }
        .course.on {
          box-shadow: 0 0 0 1px var(--primary), var(--shadow-soft);
          border-color: transparent;
        }
        .badge-on {
          position: absolute;
          top: 14px;
          right: 14px;
          font-size: 13px;
          font-weight: 600;
          color: var(--primary);
          background: color-mix(in srgb, var(--primary) 12%, var(--card));
          padding: 3px 8px;
          border-radius: 999px;
        }
        .glyph {
          width: 34px;
          height: 34px;
          display: grid;
          place-items: center;
          border-radius: 10px;
          background: color-mix(in srgb, var(--primary) 12%, var(--card));
          color: var(--primary);
          margin-bottom: 12px;
        }
        .course h3 {
          font-size: 15px;
          font-weight: 700;
          padding-right: 64px;
          line-height: 1.35;
        }
        .src {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13.5px;
          color: var(--muted-foreground);
          margin: 8px 0 12px;
        }
        .stat {
          font-size: 13.5px;
          color: var(--muted-foreground);
        }
        .stat b {
          color: var(--foreground);
          font-weight: 700;
        }
        .mbar {
          height: 6px;
          border-radius: 999px;
          background: var(--muted);
          overflow: hidden;
          margin: 7px 0 12px;
        }
        .mbar i {
          display: block;
          height: 100%;
          border-radius: 999px;
          background: var(--primary);
        }
        .mbar i.learning {
          background: var(--m-learning);
        }
        .mbar i.mastered {
          background: var(--m-mastered);
        }
        .mbar i.new {
          background: var(--m-new);
        }
        .stage {
          display: flex;
          align-items: center;
          gap: 7px;
          font-size: 13.5px;
          color: var(--muted-foreground);
        }
        .stage .dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: var(--m-learning);
        }
        .course.new {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          border-style: dashed;
          min-height: 180px;
          background: transparent;
        }
        .course.new .ring {
          width: 44px;
          height: 44px;
          display: grid;
          place-items: center;
          border-radius: 50%;
          background: color-mix(in srgb, var(--primary) 12%, var(--card));
          color: var(--primary);
          margin-bottom: 12px;
        }
        .course.new p {
          font-size: 13.5px;
          color: var(--muted-foreground);
          margin-top: 6px;
          padding: 0 8px;
        }

        /* hero */
        .hero {
          display: grid;
          grid-template-columns: 1fr 316px;
          gap: 18px;
          margin-bottom: 28px;
        }
        @media (max-width: 940px) {
          .hero {
            grid-template-columns: 1fr;
          }
        }
        .village {
          border: 1px solid var(--border);
          border-radius: 18px;
          background: var(--card);
          overflow: hidden;
        }
        .village-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          padding: 16px 18px 8px;
        }
        .village-head .t {
          font-size: 16px;
          font-weight: 700;
        }
        .village-head .sub {
          font-size: 13.5px;
          color: var(--muted-foreground);
          margin-top: 3px;
          max-width: 340px;
        }
        .counts {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          justify-content: flex-end;
        }
        .cpill {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-size: 13.5px;
          font-weight: 600;
          padding: 4px 9px;
          border-radius: 999px;
          background: var(--muted);
          color: var(--foreground);
        }
        .cpill.due {
          background: var(--accent);
          color: var(--accent-foreground);
        }
        .cpill .sw {
          width: 9px;
          height: 9px;
          border-radius: 50%;
        }
        .sw.mastered {
          background: var(--m-mastered);
        }
        .sw.learning {
          background: var(--m-learning);
        }
        .sw.new {
          background: var(--m-new);
        }
        .graph {
          width: 100%;
          height: auto;
          display: block;
          padding: 4px 8px 8px;
        }
        .village-state {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          min-height: 260px;
          color: var(--muted-foreground);
          font-size: 13.5px;
          padding: 20px;
          text-align: center;
        }
        .village-state.err {
          color: var(--destructive);
        }
        .edge {
          stroke: var(--border);
          stroke-width: 1.6;
        }
        .node {
          cursor: pointer;
        }
        .node .membrane {
          fill: none;
          opacity: 0.5;
          stroke-width: 1.4;
        }
        .node .cell {
          stroke-width: 2.4;
        }
        .node.mastered .membrane {
          stroke: var(--m-mastered);
        }
        .node.mastered .cell {
          stroke: var(--m-mastered);
          fill: color-mix(in srgb, var(--m-mastered) 20%, var(--card));
        }
        .node.mastered .core {
          fill: var(--m-mastered);
        }
        .node.learning .membrane {
          stroke: var(--m-learning);
        }
        .node.learning .cell {
          stroke: var(--m-learning);
          fill: color-mix(in srgb, var(--m-learning) 20%, var(--card));
        }
        .node.learning .core {
          fill: var(--m-learning);
        }
        .node.new .cell {
          stroke: var(--m-new);
          fill: color-mix(in srgb, var(--m-new) 12%, var(--card));
          stroke-dasharray: 4 4;
        }
        .node.new .core {
          fill: var(--m-new);
        }
        .node.sel .cell {
          filter: drop-shadow(0 0 0 3px var(--ring));
        }
        .node .lbl {
          text-anchor: middle;
          font-size: 13px;
          font-weight: 600;
          fill: var(--foreground);
        }
        .legend {
          display: flex;
          gap: 14px;
          padding: 0 18px 14px;
          font-size: 13.5px;
          color: var(--muted-foreground);
        }
        .legend span {
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .legend .ln {
          width: 16px;
          height: 0;
          border-top: 1.6px solid var(--border);
        }

        /* right rail */
        .hrail {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .panel {
          border: 1px solid var(--border);
          border-radius: 16px;
          background: var(--card);
          padding: 15px;
        }
        .kp-name {
          font-size: 15px;
          font-weight: 700;
        }
        .kp-meta {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 8px 0;
        }
        .tag {
          font-size: 13px;
          font-weight: 700;
          padding: 3px 9px;
          border-radius: 999px;
        }
        .tag.mastered {
          background: color-mix(in srgb, var(--m-mastered) 18%, var(--card));
          color: var(--m-mastered);
        }
        .tag.learning {
          background: color-mix(in srgb, var(--m-learning) 20%, var(--card));
          color: var(--m-learning);
        }
        .tag.new {
          background: var(--muted);
          color: var(--muted-foreground);
        }
        .kp-type {
          font-size: 13.5px;
          color: var(--muted-foreground);
        }
        .mrow {
          display: flex;
          justify-content: space-between;
          font-size: 13.5px;
          color: var(--muted-foreground);
          margin-bottom: 6px;
        }
        .mrow b {
          color: var(--foreground);
        }
        .kp-mod {
          margin-top: 10px;
          font-size: 13.5px;
          color: var(--muted-foreground);
        }
        .kp-book-cta {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin-top: 12px;
          padding: 6px 0;
          border: 0;
          background: transparent;
          color: var(--primary);
          font-size: 13.5px;
          font-weight: 700;
          cursor: pointer;
        }
        .kp-empty {
          font-size: 13.5px;
          color: var(--muted-foreground);
          padding: 8px 2px;
        }
        .panel.next .action {
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: var(--muted-foreground);
        }
        .panel.next .kpname {
          font-size: 15px;
          font-weight: 700;
          color: var(--primary);
          margin: 4px 0 6px;
        }
        .panel.next .reason {
          font-size: 13.5px;
          color: var(--muted-foreground);
          line-height: 1.45;
        }
        .next-complete {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 10px;
          color: var(--primary);
          font-size: 13.5px;
          font-weight: 700;
        }
        .thr {
          height: 6px;
          border-radius: 999px;
          background: var(--muted);
          overflow: hidden;
          margin-top: 10px;
        }
        .thr i {
          display: block;
          height: 100%;
          background: var(--primary);
          border-radius: 999px;
        }
        .launch {
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 15px;
          background: linear-gradient(180deg, var(--secondary), var(--card));
        }
        .attachrow {
          margin: 10px 0;
        }
        .attach {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 13.5px;
          color: var(--muted-foreground);
          background: transparent;
          border: 1px dashed var(--border);
          border-radius: 8px;
          padding: 6px 10px;
          cursor: not-allowed;
        }
        .start {
          width: 100%;
          height: 46px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          border: none;
          border-radius: 12px;
          background: var(--primary);
          color: var(--primary-foreground);
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          transition: opacity 0.15s var(--ease);
        }
        .study-link {
          display: block;
          width: fit-content;
          margin: 10px auto 0;
          padding: 2px;
          border: 0;
          background: transparent;
          color: var(--muted-foreground);
          font-size: 13.5px;
          text-decoration: underline;
          text-underline-offset: 3px;
          cursor: pointer;
        }
        .study-link:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
        .start:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }

        /* lower cards */
        .grid4 {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 16px;
        }
        @media (max-width: 940px) {
          .grid4 {
            grid-template-columns: 1fr;
          }
        }
        .col-card {
          border: 1px solid var(--border);
          border-radius: 16px;
          background: var(--card);
          padding: 15px;
        }
        .ch {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 700;
          margin-bottom: 12px;
        }
        .col-empty {
          font-size: 13.5px;
          color: var(--muted-foreground);
          padding: 12px 2px;
        }
        .col-empty.sm {
          padding: 6px 2px;
        }
        .note-item {
          display: flex;
          gap: 10px;
          width: 100%;
          text-align: left;
          padding: 9px 6px;
          border-radius: 10px;
          background: transparent;
          cursor: pointer;
          transition: background 0.15s var(--ease);
        }
        .note-item:hover {
          background: var(--muted);
        }
        .ni {
          width: 30px;
          height: 30px;
          flex: none;
          display: grid;
          place-items: center;
          border-radius: 8px;
          background: color-mix(in srgb, var(--spark) 14%, var(--card));
          color: var(--spark);
        }
        .ni.chat {
          background: color-mix(in srgb, var(--primary) 13%, var(--card));
          color: var(--primary);
        }
        .nb {
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-width: 0;
        }
        .nt {
          font-size: 13.5px;
          font-weight: 600;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .chat-preview {
          overflow: hidden;
          color: var(--muted-foreground);
          font-size: 13.5px;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .nk {
          display: flex;
          align-items: center;
          gap: 5px;
          flex-wrap: wrap;
        }
        .kbchip {
          font-size: 13px;
          padding: 2px 7px;
          border-radius: 999px;
          background: color-mix(in srgb, var(--primary) 10%, var(--card));
          color: var(--primary);
        }
        .kbchip.muted {
          background: var(--muted);
          color: var(--muted-foreground);
        }
        .np {
          font-size: 13px;
          color: var(--muted-foreground);
        }
        .due-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 9px 6px;
        }
        .due-item .sw {
          width: 9px;
          height: 9px;
          border-radius: 50%;
          flex: none;
        }
        .dn {
          display: flex;
          flex-direction: column;
          min-width: 0;
          flex: 1;
        }
        .dn b {
          font-size: 13.5px;
          font-weight: 600;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .dn em {
          font-size: 13px;
          font-style: normal;
          color: var(--muted-foreground);
        }
        .due-cta {
          font-size: 13.5px;
          font-weight: 600;
          padding: 6px 12px;
          border-radius: 8px;
          border: 1px solid var(--border);
          background: var(--card);
          color: var(--primary);
          cursor: pointer;
        }
        .due-cta:hover {
          border-color: var(--primary);
        }
        .memory-card {
          background: linear-gradient(180deg, var(--secondary), var(--card));
        }
        .mem-summary {
          padding: 4px 2px 12px;
        }
        .ms-line {
          font-size: 13.5px;
        }
        .ms-line b {
          color: var(--primary);
          font-weight: 800;
        }
        .ms-sub {
          font-size: 13.5px;
          color: var(--muted-foreground);
          margin-top: 3px;
        }
        .tidy-head {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13.5px;
          font-weight: 600;
          color: var(--muted-foreground);
          margin-bottom: 8px;
        }
        .tidy-head .cnt {
          font-size: 13px;
          font-weight: 700;
          color: var(--spark);
          background: color-mix(in srgb, var(--spark) 14%, var(--card));
          padding: 1px 8px;
          border-radius: 999px;
        }
        .tidy {
          display: flex;
          align-items: center;
          gap: 9px;
          padding: 8px 6px;
          border-radius: 10px;
        }
        .tidy:hover {
          background: var(--muted);
        }
        .ty-ic {
          width: 26px;
          height: 26px;
          flex: none;
          display: grid;
          place-items: center;
          border-radius: 7px;
        }
        .ty-ic.backlog {
          background: color-mix(in srgb, var(--primary) 14%, var(--card));
          color: var(--primary);
        }
        .ty-ic.orphan {
          background: color-mix(in srgb, var(--spark) 14%, var(--card));
          color: var(--spark);
        }
        .ty-ic.idle {
          background: color-mix(in srgb, var(--m-learning) 18%, var(--card));
          color: var(--m-learning);
        }
        .ty-b {
          display: flex;
          flex-direction: column;
          min-width: 0;
          flex: 1;
        }
        .ty-t {
          font-size: 13.5px;
          font-weight: 600;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .ty-d {
          font-size: 13px;
          color: var(--muted-foreground);
        }
        .ty-act {
          display: flex;
          gap: 5px;
          flex: none;
        }
        .ty-y,
        .ty-n {
          width: 26px;
          height: 26px;
          display: grid;
          place-items: center;
          border-radius: 7px;
          border: 1px solid var(--border);
          background: var(--card);
          cursor: pointer;
        }
        .ty-y {
          color: var(--m-mastered);
        }
        .ty-y:hover {
          border-color: var(--m-mastered);
        }
        .ty-n {
          color: var(--muted-foreground);
        }
        .ty-n:hover {
          border-color: var(--destructive);
          color: var(--destructive);
        }
      `}</style>
    </div>
  );
}
